<template>
  <div class="tin-root">
    <div class="ambient"></div>
    <div class="ambient-floor"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <div class="page">

      <!-- ── PROFILE HERO ── -->
      <div class="profile-hero">
        <div class="avatar-wrap">
          <div class="avatar">
            <img v-if="user.image" :src="user.image" :alt="user.fullName" />
            <span v-else>{{ (user.fullName || 'M').charAt(0) }}</span>
          </div>
        </div>

        <div class="profile-info">
          <p class="profile-eyebrow">Member Profile</p>
          <h1 class="profile-name">{{ user.fullName || 'Member' }}</h1>
          <p class="profile-title">
            {{ user.title || 'Member' }}
            <span v-if="user.club">&nbsp;·&nbsp;{{ user.club }}</span>
          </p>
          <p v-if="user.description" class="profile-desc">{{ user.description }}</p>

          <!-- Stats -->
          <div class="stats-row">
            <div class="stat">
              <span class="stat-num">{{ connections.length }}</span>
              <span class="stat-label">Connections</span>
            </div>
            <div class="stat-div"></div>
            <div class="stat">
              <span class="stat-num">{{ chapters.length }}</span>
              <span class="stat-label">Chapters</span>
            </div>
            <div class="stat-div"></div>
            <div class="stat">
              <span class="stat-num">{{ recommendations.length }}</span>
              <span class="stat-label">Recommendations</span>
            </div>
          </div>

          <!-- Info fields -->
          <div class="info-grid">
            <div class="info-row">
              <span class="info-label">Email</span>
              <span class="info-val">{{ user.email || 'N/A' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Home Club</span>
              <span class="info-val">{{ user.club || 'N/A' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Company</span>
              <span class="info-val">{{ user.company || 'N/A' }}</span>
            </div>
            <div class="info-row" v-if="user.jobTitle">
              <span class="info-label">Job Title</span>
              <span class="info-val">{{ user.jobTitle }}</span>
            </div>
            <div class="info-row" v-if="frequentCitiesDisplay">
              <span class="info-label">Frequent Cities</span>
              <span class="info-val">{{ frequentCitiesDisplay }}</span>
            </div>
          </div>

          <!-- Actions -->
          <div class="hero-btns">
            <button class="btn-logout" @click="logout" title="Logout">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            </button>
          </div>
        </div>
      </div>

      <!-- ── CITIES I HAVE INTEL IN ── -->
      <div class="section-wrap" v-if="favoriteCities.length">
        <div class="section-hd">
          <div class="section-rule l"></div>
          <span class="section-eyebrow">Travel Intelligence</span>
          <div class="section-rule"></div>
        </div>
        <h2 class="section-title">Cities I Have <em>Intel In</em></h2>

        <div class="cities-row">
          <button
            v-for="city in favoriteCities"
            :key="city"
            class="city-chip"
            @click="goToCityInExplore(city)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 21s-7-6.2-7-11.5A7 7 0 0119 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.3"/></svg>
            <span>{{ city }}</span>
          </button>
        </div>
      </div>

      <!-- ── MY CHAPTERS ── -->
      <div class="section-wrap">
        <div class="section-hd">
          <div class="section-rule l"></div>
          <span class="section-eyebrow">City Chapters</span>
          <div class="section-rule"></div>
        </div>
        <h2 class="section-title">My <em>Chapters</em></h2>

        <div v-if="chapters.length" class="groups-row">
          <div
            v-for="chapter in chapters"
            :key="chapter.id"
            class="group-chip"
            @click="goToChapter(chapter)"
          >
            <p class="group-name">{{ chapter.city }}</p>
            <p class="group-desc">{{ chapter.description || 'No tagline yet' }}</p>
          </div>
        </div>
        <div v-else class="section-empty">
          <p class="empty-sub">No chapters joined yet</p>
        </div>
      </div>

      <!-- ── MY RECOMMENDATIONS ── -->
      <div class="section-wrap">
        <div class="section-hd">
          <div class="section-rule l"></div>
          <span class="section-eyebrow">Dispatches</span>
          <div class="section-rule"></div>
        </div>
        <h2 class="section-title">My <em>Recommendations</em></h2>

        <div v-if="recommendations.length" class="posts-grid">
          <div
            v-for="post in recommendations"
            :key="post.id"
            class="post-card"
            @click="goToPost(post.id)"
          >
            <img v-if="post.imageURL" :src="post.imageURL" class="post-img" :alt="post.businessName" />
            <div v-else class="post-img-ph">
              <div class="post-diamond"></div>
            </div>
            <div class="post-body">
              <p class="post-place-tags">{{ post.city || 'Unknown City' }} · {{ post.category || 'Recommendation' }}</p>
              <p class="post-place-name">{{ post.businessName || 'Untitled Place' }}</p>
              <div class="post-meta">
                <span class="post-stat">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 2l2.9 6.26L21 9.27l-4.5 4.38L17.8 20 12 16.9 6.2 20l1.3-6.35L3 9.27l6.1-1.01L12 2z"/></svg>
                  {{ post.arrangementInterest?.length || 0 }}
                </span>
                <span class="post-stat">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                  {{ post.commentsCount || 0 }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="section-empty">
          <p class="empty-sub">No recommendations shared yet</p>
        </div>
      </div>

      <!-- ── MY CONNECTIONS ── -->
      <div class="section-wrap">
        <div class="section-hd">
          <div class="section-rule l"></div>
          <span class="section-eyebrow">The Circle</span>
          <div class="section-rule"></div>
        </div>
        <h2 class="section-title">My <em>Connections</em></h2>

        <div v-if="connections.length" class="conn-row">
          <div
            v-for="conn in connections"
            :key="conn.id"
            class="conn-item"
            @click="$router.push({ name: 'MemberDetail', params: { id: conn.id } })"
          >
            <div class="conn-avatar">
              <img v-if="conn.image" :src="conn.image" :alt="conn.fullName" />
              <span v-else>{{ (conn.fullName || '?').charAt(0) }}</span>
            </div>
            <span class="conn-name">{{ (conn.fullName || '').split(' ')[0] }}</span>
          </div>
        </div>
        <div v-else class="section-empty">
          <p class="empty-sub">No connections yet</p>
        </div>
      </div>

    </div>

    <!-- ── BOTTOM NAV ── -->
    <nav class="bottom-nav">
      <div class="bottom-nav-glow"></div>
      <button class="nav-item" @click="$router.push('/home')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        <span class="nav-label">Home</span>
      </button>
      <button class="nav-item" @click="$router.push('/search')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <span class="nav-label">Search</span>
      </button>
      <button class="nav-item" @click="$router.push('/explore')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
        <span class="nav-label">Explore</span>
      </button>
      <button class="nav-item" @click="$router.push('/messages')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        <span class="nav-label">Messages</span>
      </button>
      <button class="nav-item" @click="$router.push('/groups')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
        <span class="nav-label">Groups</span>
      </button>
      <button class="nav-item active">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        <span class="nav-label">Profile</span>
        <div class="nav-dot"></div>
      </button>
    </nav>
  </div>
</template>

<script>
import { getAuth, signOut } from "firebase/auth";
import { doc, getDoc, collection, getDocs, query, where } from "firebase/firestore";
import { db } from "@/firebase";

export default {
  name: "ProfileView",

  data() {
    return {
      user: {},
      chapters: [],
      recommendations: [],
      connections: [],
    };
  },

  computed: {
    favoriteCities() {
      const set = new Set(this.recommendations.map((p) => p.city).filter(Boolean));
      return [...set].sort();
    },

    // frequentCities is now stored as a real array; existing users who
    // registered before that existed still have it as the old
    // comma-separated string. Both need to display as a clean
    // ", "-joined list, not JS's default Array.toString() (a bare
    // comma, no spaces) if interpolated directly.
    frequentCitiesDisplay() {
      const raw = this.user.frequentCities;
      if (Array.isArray(raw)) return raw.filter(Boolean).join(", ");
      return raw || "";
    },
  },

  async mounted() {
    await this.fetchUserData();
    await Promise.all([
      this.fetchChapters(),
      this.fetchConnections(),
    ]);
  },

  methods: {
    async fetchUserData() {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      const userSnap = await getDoc(doc(db, "user", currentUser.uid));
      if (userSnap.exists()) {
        this.user = userSnap.data();
        await this.fetchUserRecommendations(currentUser.uid);
      }
    },

    async fetchChapters() {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      const groupsSnap = await getDocs(collection(db, "groups"));
      this.chapters = groupsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((g) => g.members?.includes(currentUser.uid));
    },

    async fetchUserRecommendations(uid) {
      const postsSnap = await getDocs(collection(db, "explore"));
      this.recommendations = postsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((p) => p.uid === uid);
    },

    async fetchConnections() {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      const connSnap = await getDocs(
        collection(db, `user/${currentUser.uid}/connections`)
      );
      const rawConnections = connSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Same fix as HomeView.vue / MemberFriendsView.vue — resolve against the
      // live user doc instead of trusting denormalized fields that can be
      // missing or stale, and drop the connection entirely if the account
      // is gone rather than falling back to the raw doc (which is how a
      // deleted account was showing up here as a blank "?" avatar with no
      // name). Fixing deleteMyAccount to also clean up the stale doc itself
      // (see index.ts) means this filter is mostly a safety net for
      // connections that existed before that fix shipped.
      const resolvedConnections = await Promise.all(
        rawConnections.map(async (conn) => {
          try {
            const userSnap = await getDoc(doc(db, "user", conn.id));
            if (userSnap.exists()) {
              const userData = userSnap.data();
              return { ...conn, fullName: userData.fullName || conn.fullName || "", image: userData.image || conn.image || "" };
            }
            return null;
          } catch (e) {
            console.error("Failed to resolve connection profile:", e);
            return conn;
          }
        })
      );
      this.connections = resolvedConnections.filter(Boolean);
    },

    logout() {
      const auth = getAuth();
      signOut(auth).then(() => this.$router.push("/"));
    },

    goToChapter(chapter) {
      this.$router.push(`/groups/${encodeURIComponent(chapter.city)}`);
    },

    goToCityInExplore(city) {
      this.$router.push({ path: "/explore", query: { city } });
    },

    goToPost(postID) {
      this.$router.push({ name: "PostDetail", params: { postId: postID } });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

.tin-root { position: relative; min-height: 100vh; width: 100%; background: #070708; font-family: 'Montserrat', sans-serif; }

.ambient { position: fixed; top: -300px; left: 50%; transform: translateX(-50%); width: 1000px; height: 800px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.09) 0%, rgba(210,175,100,0.03) 30%, transparent 65%); }
.ambient-floor { position: fixed; bottom: -200px; left: 50%; transform: translateX(-50%); width: 700px; height: 400px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 100%, rgba(210,175,100,0.03) 0%, transparent 60%); }
.grain { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.5; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E"); background-size: 180px 180px; }
.frame { position: fixed; inset: 20px; border: 1px solid rgba(210,175,100,0.07); z-index: 2; pointer-events: none; }
.frame::before, .frame::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 20px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after  { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }

.page { position: relative; z-index: 10; max-width: 860px; margin: 0 auto; padding: 52px 48px 140px; animation: fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

/* ── HERO ── */
.profile-hero { display: grid; grid-template-columns: 160px 1fr; gap: 52px; align-items: start; padding-bottom: 52px; border-bottom: 1px solid rgba(210,175,100,0.1); margin-bottom: 56px; }
.avatar { width: 160px; height: 160px; border-radius: 50%; border: 1px solid rgba(210,175,100,0.3); overflow: hidden; background: rgba(210,175,100,0.05); display: flex; align-items: center; justify-content: center; font-family: 'Cormorant', serif; font-size: 56px; font-weight: 300; color: rgba(210,175,100,0.3); flex-shrink: 0; }
.avatar img { width: 100%; height: 100%; object-fit: cover; }

.profile-info { padding-top: 4px; }
.profile-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.45); text-transform: uppercase; margin-bottom: 8px; }
.profile-name { font-family: 'Cormorant', serif; font-size: 44px; font-weight: 300; color: #edeae2; letter-spacing: -1px; line-height: 1; margin-bottom: 8px; }
.profile-title { font-size: 11px; font-weight: 500; letter-spacing: 2.5px; color: rgba(210,175,100,0.5); text-transform: uppercase; margin-bottom: 20px; }
.profile-desc { font-size: 13px; font-weight: 300; color: rgba(237,232,223,0.35); line-height: 1.8; letter-spacing: 0.2px; margin-bottom: 24px; max-width: 440px; }

.stats-row { display: flex; gap: 28px; align-items: center; margin-bottom: 28px; }
.stat { display: flex; flex-direction: column; gap: 3px; }
.stat-num { font-family: 'Cormorant', serif; font-size: 30px; font-weight: 300; color: rgba(210,175,100,0.75); line-height: 1; }
.stat-label { font-size: 8px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(237,232,223,0.2); }
.stat-div { width: 1px; height: 36px; background: rgba(210,175,100,0.12); }

.info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 32px; margin-bottom: 28px; }
.info-row { display: flex; flex-direction: column; gap: 3px; padding: 10px 0; border-bottom: 1px solid rgba(210,175,100,0.07); }
.info-label { font-size: 8px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: rgba(210,175,100,0.35); }
.info-val { font-size: 13px; font-weight: 300; color: rgba(237,232,223,0.55); letter-spacing: 0.2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.hero-btns { display: flex; align-items: center; gap: 12px; }
.btn-logout { background: transparent; border: 1px solid rgba(237,232,223,0.07); width: 44px; height: 44px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: rgba(237,232,223,0.2); transition: border-color 0.2s, color 0.2s; flex-shrink: 0; }
.btn-logout:hover { border-color: rgba(180,60,60,0.4); color: rgba(190,70,70,0.7); }
.btn-logout svg { width: 16px; height: 16px; }

/* ── SECTION HEADERS ── */
.section-hd { display: flex; align-items: center; gap: 20px; margin-bottom: 16px; }
.section-rule { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent); }
.section-rule.l { background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18)); }
.section-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.45); text-transform: uppercase; white-space: nowrap; }
.section-title { font-family: 'Cormorant', serif; font-size: 34px; font-weight: 300; color: #edeae2; letter-spacing: -0.5px; margin-bottom: 24px; line-height: 1; }
.section-title em { font-style: italic; color: rgba(210,175,100,0.8); font-weight: 300; }
.section-wrap { margin-bottom: 56px; }
.section-empty { padding: 24px 0; }
.empty-sub { font-size: 11px; font-weight: 400; letter-spacing: 1.5px; color: rgba(237,232,223,0.15); text-transform: uppercase; }

/* ── CITIES STRIP ── */
.cities-row { display: flex; gap: 10px; flex-wrap: wrap; }
.city-chip { display: inline-flex; align-items: center; gap: 8px; border: 1px solid rgba(210,175,100,0.2); background: rgba(210,175,100,0.03); padding: 10px 16px; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 500; letter-spacing: 0.5px; color: rgba(237,232,223,0.6); transition: border-color 0.2s, color 0.2s, background 0.2s; }
.city-chip:hover { border-color: rgba(210,175,100,0.5); color: rgba(210,175,100,0.9); background: rgba(210,175,100,0.08); }
.city-chip svg { width: 13px; height: 13px; color: rgba(210,175,100,0.5); flex-shrink: 0; }

/* ── GROUPS ── */
.groups-row { display: flex; gap: 14px; overflow-x: auto; padding-bottom: 8px; scrollbar-width: none; }
.groups-row::-webkit-scrollbar { display: none; }
.group-chip { border: 1px solid rgba(210,175,100,0.1); background: rgba(210,175,100,0.02); padding: 16px 20px; cursor: pointer; transition: border-color 0.2s; white-space: nowrap; min-width: 160px; position: relative; flex-shrink: 0; }
.group-chip::before, .group-chip::after { content: ''; position: absolute; width: 10px; height: 10px; }
.group-chip::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.3); border-left: 1px solid rgba(210,175,100,0.3); }
.group-chip::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.3); border-right: 1px solid rgba(210,175,100,0.3); }
.group-chip:hover { border-color: rgba(210,175,100,0.3); }
.group-name { font-family: 'Cormorant', serif; font-size: 18px; font-weight: 400; color: #edeae2; margin-bottom: 4px; line-height: 1.2; }
.group-desc { font-size: 9px; font-weight: 400; color: rgba(237,232,223,0.2); letter-spacing: 0.3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 160px; }

/* ── POSTS ── */
.posts-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.post-card { border: 1px solid rgba(210,175,100,0.1); background: rgba(210,175,100,0.02); cursor: pointer; transition: border-color 0.2s; position: relative; overflow: hidden; }
.post-card::before, .post-card::after { content: ''; position: absolute; width: 10px; height: 10px; z-index: 1; }
.post-card::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.25); border-left: 1px solid rgba(210,175,100,0.25); }
.post-card::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.25); border-right: 1px solid rgba(210,175,100,0.25); }
.post-card:hover { border-color: rgba(210,175,100,0.3); }
.post-img { width: 100%; height: 140px; object-fit: cover; display: block; filter: brightness(0.82) saturate(0.7); }
.post-img-ph { width: 100%; height: 140px; background: rgba(210,175,100,0.04); display: flex; align-items: center; justify-content: center; }
.post-diamond { width: 20px; height: 20px; border: 1px solid rgba(210,175,100,0.15); transform: rotate(45deg); }
.post-body { padding: 12px 14px; }
.post-place-tags { font-size: 8.5px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(210,175,100,0.5); margin-bottom: 6px; }
.post-place-name { font-family: 'Cormorant', serif; font-size: 17px; font-weight: 400; color: #edeae2; line-height: 1.25; margin-bottom: 10px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.post-meta { display: flex; gap: 16px; }
.post-stat { font-size: 9px; font-weight: 600; letter-spacing: 1.5px; color: rgba(210,175,100,0.3); display: flex; align-items: center; gap: 5px; }
.post-stat svg { width: 11px; height: 11px; }

/* ── CONNECTIONS ── */
.conn-row { display: flex; gap: 24px; flex-wrap: wrap; }
.conn-item { display: flex; flex-direction: column; align-items: center; gap: 8px; cursor: pointer; transition: opacity 0.2s; }
.conn-item:hover { opacity: 0.75; }
.conn-avatar { width: 64px; height: 64px; border-radius: 50%; border: 1px solid rgba(210,175,100,0.2); overflow: hidden; background: rgba(210,175,100,0.04); display: flex; align-items: center; justify-content: center; font-family: 'Cormorant', serif; font-size: 22px; font-weight: 300; color: rgba(210,175,100,0.4); transition: border-color 0.2s; }
.conn-item:hover .conn-avatar { border-color: rgba(210,175,100,0.5); }
.conn-avatar img { width: 100%; height: 100%; object-fit: cover; object-position: top; }
.conn-name { font-size: 10px; font-weight: 400; color: rgba(237,232,223,0.25); text-align: center; letter-spacing: 0.3px; }

/* ── BOTTOM NAV ── */
.bottom-nav { position: fixed; bottom: 0; left: 0; right: 0; z-index: 20; background: #070708; border-top: 1px solid rgba(210,175,100,0.1); display: flex; align-items: center; justify-content: space-around; padding: 14px 0 18px; }
.bottom-nav-glow { position: absolute; top: -1px; left: 10%; right: 10%; height: 1px; background: linear-gradient(90deg, transparent, rgba(210,175,100,0.2), transparent); pointer-events: none; }
.nav-item { display: flex; flex-direction: column; align-items: center; gap: 5px; cursor: pointer; padding: 4px 20px; color: rgba(237,232,223,0.2); transition: color 0.2s; background: none; border: none; font-family: 'Montserrat', sans-serif; min-width: 60px; }
.nav-item.active { color: rgba(210,175,100,0.8); }
.nav-item:hover:not(.active) { color: rgba(237,232,223,0.45); }
.nav-item svg { width: 18px; height: 18px; }
.nav-label { font-size: 9px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; }
.nav-dot { width: 3px; height: 3px; border-radius: 50%; background: rgba(210,175,100,0.7); margin-top: 1px; }

/* ── RESPONSIVE ── */
@media (max-width: 700px) { .profile-hero { grid-template-columns: 1fr; gap: 28px; } .avatar { width: 120px; height: 120px; font-size: 40px; } .profile-name { font-size: 36px; } .posts-grid { grid-template-columns: 1fr; } .page { padding: 36px 24px 120px; } .frame, .frame-inner { inset: 12px; } }
@media (max-width: 480px) { .info-grid { grid-template-columns: 1fr; } .nav-item { padding: 4px 12px; } }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
</style>